/**
 * <tk-hero-rotator> — rotace slidů uvnitř jednoho slotu hero gridu.
 *
 * Sám o sobě nic nekreslí: slidy jsou server-rendered v Liquidu (viz
 * sections/tk-hero-grid.liquid), tady se jen přepíná, který je aktivní.
 *
 * DVA REŽIMY PŘECHODU, schválně:
 *   1) `go()` — autoplay, tečky, chevrony. Přechod dělá CSS @keyframes podle
 *      data-effect (prolnutí / posun / zoom / skok). Animují se vždy jen DVA
 *      slidy (odcházející + přicházející), takže přetočení z posledního na
 *      první vypadá stejně jako každé jiné.
 *   2) tažení — slide jde 1:1 za prstem a soused se odkrývá vedle něj.
 *      ⚠️ Tažení je VŽDYCKY vodorovné, bez ohledu na nastavený efekt: gesto je
 *      prostorové, „prolnutí za prstem" nedává smysl. Nastavený efekt se
 *      použije jen v režimu 1.
 *
 * Atributy (sype je sekce ze settings):
 *   data-effect        fade | slide | slide-v | zoom | none
 *   data-interval      ms mezi přehozeními (0 = neautoplayuje)
 *   data-speed         ms délka přechodu (jde i do CSS var --tk-rot-speed)
 *   data-pause-hover   přítomen = pauza při hoveru
 *
 * Sám se zastaví když: je mimo viewport, je skrytá záložka, uživatel
 * hoveruje/tabuje dovnitř, táhne, nebo má prefers-reduced-motion. Ruční
 * přehození (tečka/šipka/tažení) autoplay jen odloží — viz `takeOver()`.
 */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  // Jak dlouho po ručním ovládání zůstat stát, než se autoplay zase rozjede.
  // Napevno, ne násobek intervalu: pomalejší slot by čekal úměrně dýl, a přesně
  // to působilo, že se rotace „nerozjela". Jediné číslo na ladění.
  const RESUME_AFTER = 3000;
  // Práh, od kterého je pohyb tažením a ne klepnutím. Dotyk má větší vůli než
  // myš — prst při klepnutí na telefonu běžně ujede 5-10 px.
  const LOCK_SLOP = { mouse: 6, touch: 14 };
  // Až od téhle ujeté dráhy se po puštění potlačí klik na odkaz. Schválně výš
  // než LOCK_SLOP: rozklepnuté klepnutí slidem jen cukne a odkaz PROJDE.
  const CLICK_SLOP = 22;

  class TkHeroRotator extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.tk-hero-slot__slide'));
      if (this.slides.length < 2) return; // jeden slide = statický banner, žádná režie

      this.dots = Array.from(this.querySelectorAll('.tk-hero-slot__dot'));
      this.index = Math.max(0, this.slides.findIndex((s) => s.hasAttribute('data-active')));
      this.interval = parseInt(this.dataset.interval || '0', 10);
      this.speed = parseInt(this.dataset.speed || '600', 10);
      this.animating = false;
      this.busy = false;
      this.timer = null;

      // Důvody, proč zrovna teď stojíme. Autoplay běží jen když je množina
      // prázdná — hover tak nemůže „odpauzovat" to, co uspal IntersectionObserver.
      this.holds = new Set();

      this.onEnd = this.onEnd.bind(this);
      this.bind();
      this.primeImages();
      this.maybeStart();
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.failsafe);
      clearTimeout(this.settleT);
      clearTimeout(this.resumeT);
      if (this.io) this.io.disconnect();
      // Theme editor sekce překresluje, takže odhlásit posluchače na document
      // není kosmetika — jinak by po pár úpravách běželo N mrtvých kopií.
      if (this.onVis) document.removeEventListener('visibilitychange', this.onVis);
      if (this.onSelect) document.removeEventListener('shopify:block:select', this.onSelect);
      if (this.onDeselect) document.removeEventListener('shopify:block:deselect', this.onDeselect);
    }

    /**
     * ⚠️ Slidy 2..N mají v Liquidu `loading="lazy"` a leží ve `visibility:
     * hidden`, takže si je prohlížeč sám NIKDY nestáhne — dokud je neukážeme.
     * Při tažení nebo přehození se pak místo banneru objevila prázdná plocha.
     *
     * Necháváme je proto v HTML lazy (kvůli LCP — hero vlevo je LCP prvek
     * a nesmí soupeřit o pásmo) a teprve až je prohlížeč nečinný, přepneme je
     * na eager a předekódujeme. Do první interakce je čas, po ní je hotovo.
     */
    primeImages() {
      const prime = () => {
        for (const slide of this.slides) {
          const img = slide.querySelector('img');
          if (!img || img.dataset.tkPrimed) continue;
          img.dataset.tkPrimed = '1';
          img.loading = 'eager';
          if (img.decode) img.decode().catch(() => {});
        }
      };
      if ('requestIdleCallback' in window) requestIdleCallback(prime, { timeout: 3000 });
      else setTimeout(prime, 1200);
    }

    bind() {
      this.querySelectorAll('[data-tk-rot]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const to = btn.dataset.tkRot;
          this.interrupt(); // rychlé klikání po tečkách nemá co zahazovat
          this.takeOver();
          if (to === 'prev') this.go(this.index - 1, 'prev');
          else if (to === 'next') this.go(this.index + 1, 'next');
          else {
            const i = parseInt(to, 10);
            if (!Number.isNaN(i)) this.go(i, i > this.index ? 'next' : 'prev');
          }
        });
      });

      if (this.hasAttribute('data-pause-hover')) {
        this.addEventListener('mouseenter', () => this.hold('hover'));
        this.addEventListener('mouseleave', () => this.release('hover'));
      }
      // Fokus uvnitř pauzuje vždy: kdo tabuje po odkazech, nesmí přijít o cíl.
      this.addEventListener('focusin', () => this.hold('focus'));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.release('focus');
      });

      this.onVis = () => (document.hidden ? this.hold('tab') : this.release('tab'));
      document.addEventListener('visibilitychange', this.onVis);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          ([entry]) => (entry.isIntersecting ? this.release('offscreen') : this.hold('offscreen')),
          { threshold: 0.1 }
        );
        this.io.observe(this);
      }

      this.drag();
      this.wheelNav();

      // Theme editor: kliknutí na blok v panelu přepne na ten slide a nechá ho
      // stát. Událost je na document, cizí blok si každý rotátor odfiltruje sám.
      this.onSelect = (e) => {
        const el = e.target instanceof Element && e.target.closest('.tk-hero-slot__slide');
        const i = el ? this.slides.indexOf(el) : -1;
        if (i < 0) return;
        this.stop();
        this.go(i, i > this.index ? 'next' : 'prev');
      };
      this.onDeselect = () => this.maybeStart();
      document.addEventListener('shopify:block:select', this.onSelect);
      document.addEventListener('shopify:block:deselect', this.onDeselect);
    }

    /**
     * Tažení prstem i myší, jeden kód přes Pointer Events.
     *
     * ⚠️ Svislý scroll blokuje CSS `touch-action: pan-y`, ne preventDefault.
     * Prohlížeč si nechá svislé posouvání (a pošle pointercancel), vodorovné
     * gesto propustí k nám a stránkou u toho nehne.
     *
     * ⚠️ Zápis transformů je škrcený na requestAnimationFrame a slidy dostanou
     * na dobu gesta `will-change: transform` (viz arm()). Bez toho jde o obyčejný
     * neprovrstvený element a prohlížeč každý snímek PŘEKRESLUJE celou dlaždici
     * (fotka + text + stín) — na desktopu to je přesně ta „sekaná" animace.
     * S vrstvou je tažení jen posun hotové textury po kompozitoru.
     *
     * ⚠️ `setPointerCapture` se volá JEN pro myš. U dotyku má prohlížeč
     * implicitní capture na elementu, který dostal pointerdown (tedy na <a>
     * slidu) — kdybychom capture přebrali na rotátor, ten <a> ho ztratí,
     * vystřelí `lostpointercapture`, ta BUBLÁ nahoru k rotátoru a shodí nám
     * vlastní gesto. Přesně tohle rozbilo swipe na iOS.
     */
    drag() {
      let x0 = null;
      let y0 = null;
      let dx = 0;
      let locked = false;
      // Největší vodorovný posun za celé gesto. Rozhoduje o tom, jestli se
      // po puštění potlačí klik — viz LOCK_SLOP / CLICK_SLOP nahoře.
      let maxAbs = 0;
      let lockSlop = LOCK_SLOP.touch;
      // Dotyk vs. myš: na dotyku se na stisk NESAHÁ na nic, viz pointerdown níž.
      let coarse = false;
      // Jestli už běží arm(true). Bez toho by arm(false) sahal na styl slidu
      // i po obyčejném klepnutí, kde se nikdy nic nezaparkovalo.
      let armed = false;
      let dir = null;
      let W = 0;
      let raf = 0;
      // Posun za prstem NEJDE přes style.transform — na téhle stránce stojí
      // jedna změna inline stylu ~66 ms, protože Chrome přepočítá styl celého
      // dokumentu (změřeno: 5 183 prvků, i pro úplně cizí prázdný <div>).
      // Místo toho drží každý pohybující se slide pozastavenou WAAPI animaci
      // a my jen posouváme její currentTime — to běží na kompozitoru a styl
      // dokumentu nešpiní. Rozdíl: 15 fps → 120 fps.
      let animCur = null;
      let animPeek = null;
      let RANGE = 0;

      const clear = () => {
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
        dropAnims();
        x0 = null;
        y0 = null;
        dx = 0;
        maxAbs = 0;
        locked = false;
        armed = false;
        dir = null;
      };

      const peekFor = (d) => {
        const n = this.slides.length;
        const i = ((this.index + (d === 'next' ? 1 : -1)) % n + n) % n;
        return this.slides[i];
      };

      /**
       * Pozastavená animace, ve které 1 ms == 1 px posunu. Střed dráhy je
       * `base`, takže `currentTime = RANGE + dx` posune slide přesně o dx.
       * `fill: 'both'` drží krajní snímek i mimo dobu běhu, takže se nic
       * neresetuje. Rozsah 1,5× šířka stačí — dál je slide stejně mimo ořez.
       */
      const makeAnim = (el, base) => {
        if (!el || !el.animate) return null;
        const a = el.animate(
          [
            { transform: `translate3d(${base - RANGE}px, 0, 0)` },
            { transform: `translate3d(${base + RANGE}px, 0, 0)` },
          ],
          { duration: RANGE * 2, fill: 'both', easing: 'linear' }
        );
        a.pause();
        a.currentTime = RANGE;
        return a;
      };

      const dropAnims = () => {
        if (animCur) { animCur.cancel(); animCur = null; }
        if (animPeek) { animPeek.cancel(); animPeek = null; }
      };

      const place = () => {
        const cur = this.slides[this.index];
        const t = Math.max(0, Math.min(RANGE * 2, RANGE + dx));
        if (animCur) animCur.currentTime = t;
        else cur.style.transform = `translate3d(${dx.toFixed(1)}px, 0, 0)`;

        if (this.peek) {
          if (animPeek) animPeek.currentTime = t;
          else {
            const base = dir === 'next' ? W : -W;
            this.peek.style.transform = `translate3d(${(base + dx).toFixed(1)}px, 0, 0)`;
          }
        }
      };

      // Myš umí posílat pointermove hustěji, než se kreslí snímky (a Safari
      // s Firefoxem je nekoalescují jako Chrome). Jeden zápis na snímek stačí —
      // víc jen nutí prohlížeč přepočítávat styl a zahazovat mezivýsledky.
      const schedule = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          if (locked) place();
        });
      };

      this.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        coarse = e.pointerType !== 'mouse';
        lockSlop = coarse ? LOCK_SLOP.touch : LOCK_SLOP.mouse;
        // ⚠️ Rozdělaný přechod se NEČEKÁ, dorazí se. Dřív tady byl `return`,
        // když ještě běžela předchozí animace — druhý swipe v rychlém sledu
        // se tím zahodil a listování působilo, že nereaguje.
        //
        // ⚠️⚠️ NA DOTYKU SE TOHLE VŠECHNO ODKLÁDÁ AŽ NA ZÁMEK GESTA. iOS si
        // ťuknutí vyhodnocuje sám a když se elementu pod prstem mezi
        // touchstart a touchend změní styl (`interrupt()` přehodí slide,
        // `arm()` sype `will-change: transform` a odkrývá sousedy), Safari
        // ťuknutí zahodí — odkaz se ani nepodbarví jako ostatní odkazy
        // a proklik neprojde. Na myši je to naopak žádoucí (vrstvy hotové
        // dřív, než přijde první pohyb), tam to zůstává.
        if (!coarse) {
          this.interrupt();
        }
        x0 = e.clientX;
        y0 = e.clientY;
        dx = 0;
        this.dragged = false;
        // Vrstvy se zakládají už na stisk, ne až na první pohyb — jinak by
        // promotion padla do stejného snímku jako první posun a gesto by
        // začalo cuknutím. Na dotyku až při zámku, viz komentář výš.
        if (!coarse) {
          this.arm(true);
          armed = true;
        }
      });

      this.addEventListener('pointermove', (e) => {
        if (x0 === null) return;
        const ax = e.clientX - x0;
        const ay = e.clientY - y0;

        if (Math.abs(ax) > maxAbs) maxAbs = Math.abs(ax);

        if (!locked) {
          if (Math.abs(ax) < lockSlop) return;
          // Převažuje-li svislá složka, gesto patří stránce — pustíme ho.
          if (Math.abs(ax) <= Math.abs(ay)) { if (armed) this.arm(false); clear(); return; }
          locked = true;
          // Dotyk: teprve TEĎ je jisté, že jde o tažení a ne o ťuknutí.
          if (coarse) {
            this.interrupt();
            this.arm(true);
            armed = true;
          }
          this.dragged = true;
          W = this.offsetWidth;
          RANGE = W * 1.5;
          animCur = makeAnim(this.slides[this.index], 0);
          this.setAttribute('data-dragging', '');
          this.hold('drag');
          if (e.pointerType === 'mouse' && this.setPointerCapture) {
            this.setPointerCapture(e.pointerId);
          }
        }

        dx = ax;
        const want = dx < 0 ? 'next' : 'prev';
        // Změna směru uprostřed gesta: starého souseda uklidit a vzít druhého.
        if (want !== dir) {
          if (this.peek) {
            if (animPeek) { animPeek.cancel(); animPeek = null; }
            this.peek.style.transform = '';
            this.peek.removeAttribute('data-peek');
          }
          dir = want;
          this.peek = peekFor(dir);
          this.peek.setAttribute('data-peek', '');
          animPeek = makeAnim(this.peek, dir === 'next' ? W : -W);
          place(); // změna souseda musí být vidět hned, ne až za snímek
        } else {
          schedule();
        }
      });

      const end = () => {
        if (x0 === null) return;
        if (!locked) { if (armed) this.arm(false); clear(); return; }
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
        place(); // poslední poloha pod prstem, ať dojezd nenavazuje na starý snímek
        // settle() žije mimo tenhle closure, tak mu stav předáme přes instanci
        this.dragAnimCur = animCur;
        this.dragAnimPeek = animPeek;
        this.dragDx = dx;
        animCur = null;
        animPeek = null;
        // Práh roste s šířkou banneru, ať se na velkém monitoru nepřepínalo
        // omylem a na malé dlaždici to nešlo ztuha.
        const commit = Math.abs(dx) >= Math.max(36, W * 0.12);
        // ⚠️ Klik se potlačuje až podle SKUTEČNĚ ujeté dráhy, ne podle toho, že
        // se gesto zamklo. Prst při klepnutí běžně ujede pár pixelů — s původním
        // prahem 8 px to stačilo na zámek, dojezd slide vrátil zpátky (nic není
        // vidět) a klik už neprošel. Na iOS to vypadalo, že malý banner jde
        // otevřít až na druhé klepnutí. Myš tím netrpí, ta neujede vůbec.
        this.dragged = maxAbs >= CLICK_SLOP;
        this.settle(commit, dir, W);
        clear();
      };

      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);
      // Nativní tažení obrázku/odkazu by na myši gesto překrylo.
      this.addEventListener('dragstart', (e) => e.preventDefault());
      // Slide je odkaz — po tažení nesmí projít klik, jinak swipe navigoval
      // na produkt. Fáze capture, ať se k <a> vůbec nedostane.
      this.addEventListener(
        'click',
        (e) => {
          if (!this.dragged) return;
          e.preventDefault();
          e.stopPropagation();
          this.dragged = false;
        },
        true
      );
    }

    /**
     * Vodorovný posun kolečkem / dvěma prsty po touchpadu.
     *
     * ⚠️ `preventDefault()` tu není kosmetika — rotátor NENÍ vodorovně
     * scrollovatelný kontejner, takže vodorovná delta propadne na stránku,
     * a protože ani ta nemá kam scrollovat, prohlížeč z toho udělá navigační
     * gesto zpět/vpřed. Zrušit to jde jedině sebráním události, což vyžaduje
     * `{ passive: false }`. (`overscroll-behavior-x` nepomůže, platí jen pro
     * skutečné scroll kontejnery.)
     *
     * ⚠️ ROZHODUJE PRVNÍ UDÁLOST GESTA. Chrome si po ní gesto zamkne na cíl
     * (scroll latching) a další `preventDefault()` už ignoruje. První událost
     * má přitom klidně jen 1 px — jakýkoli spodní práh na `dx` ji zahodí
     * a stránka cukne navigací, i když se zbytek gesta sebere správně.
     *
     * ⚠️ JEDNO GESTO = JEDEN SLIDE. Dojezd setrvačnosti se po přehození polyká
     * (`lock`) — touchpad po flicku sype delty ještě stovky ms. Zámek se ale
     * nesmí pouštět až po jejich doznění (to trvá i vteřiny a listování pak
     * působí zaseknutě), takže se hlídá, kdy jsou prsty zpátky na touchpadu:
     * delta při setrvačnosti KLESÁ, nové gesto ji nechá RŮST. Rozhodují až
     * DVĚ rostoucí delty po sobě a nejdřív po `COOLDOWN` — na jediný výkyv
     * se zámek pouštěl tak ochotně, že jedno mávnutí přehodilo čtyři slidy.
     *
     * ⚠️ Autoplay se tímhle NERUŠÍ natrvalo (na rozdíl od teček a šipek):
     * `hold('wheel')` jen pauzuje po dobu gesta, takže po odjetí myši
     * pryč se rotace zase rozjede.
     *
     * Na rozdíl od tažení prstem tohle NEJDE 1:1 za gestem — `wheel` nemá
     * konec gesta, takže by se nedalo poznat, kdy dojet zpátky a kdy commitnout.
     * Přepíná se proto přes `go()`, tedy nastaveným efektem, stejně jako šipky.
     */
    wheelNav() {
      const THRESHOLD = 50; // px nasčítané dráhy, než se přehodí
      const QUIET = 140;    // ms ticha, po kterých je gesto považované za dokončené
      const COOLDOWN = 250; // ms po přehození, kdy se polyká všechno bez výjimky
      const RISE = 4;       // px, o kolik musí delta vzrůst, aby se počítala za nové gesto
      let acc = 0;
      let lock = false;
      let lastAbs = 0;
      let rises = 0;
      let lockAt = 0;
      let idle = 0;

      const finish = () => {
        acc = 0;
        lock = false;
        lastAbs = 0;
        rises = 0;
        this.release('wheel');
      };

      const quiet = () => {
        clearTimeout(idle);
        idle = setTimeout(finish, QUIET);
      };

      this.addEventListener(
        'wheel',
        (e) => {
          // Firefox posílá u kolečka řádky (deltaMode 1), ne pixely.
          const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? this.offsetWidth : 1;
          // ⚠️ Znaménko se NEUPRAVUJE podle platformy. macOS má ve výchozím stavu
          // „natural scrolling", které deltaX obrací oproti Windows pro úplně
          // stejný pohyb prstů — jenže přesně tak se chovají i všechny nativní
          // scroll kontejnery na webu (karusely kolekcí, PDP galerie). Kdo to
          // tady obrátí, rozejde banner s nimi na téže stránce; vyzkoušeno
          // 2026-08-25 (commit 9f359b4) a zase vráceno. Komu nesedí Mac vs
          // Windows, řeší to systémové nastavení trackpadu, ne tenhle soubor.
          const dx = e.deltaX * unit;
          const abs = Math.abs(dx);

          // Převažuje-li svislá složka, gesto patří stránce — kdo nad bannerem
          // scrolluje dolů, nesmí přijít o scroll. Žádný absolutní práh navíc
          // (viz poznámka o první události výš).
          if (abs === 0 || abs < Math.abs(e.deltaY * unit)) return;

          // Nezrušitelnou událost posílá prohlížeč, když už scroll rozjel;
          // preventDefault by na ní jen zaklel do konzole.
          if (e.cancelable) e.preventDefault();
          this.hold('wheel'); // pauza autoplaye po dobu gesta (Set, takže idempotentní)
          quiet();

          if (lock) {
            rises = abs > lastAbs + RISE ? rises + 1 : 0;
            lastAbs = abs;
            if (rises < 2 || performance.now() - lockAt < COOLDOWN) return;
            lock = false; // dvě rostoucí delty po sobě = nové gesto, ne dojezd
            rises = 0;
            acc = 0; // nové gesto si musí nasbírat celý práh znovu
          }
          lastAbs = abs;

          acc += dx;
          if (Math.abs(acc) < THRESHOLD) return;

          // ⚠️ Znaménko je schválně obrácené proti nativnímu scrollu: gesto se
          // řídí TAŽENÍM, ne scrollováním. Jsou to dva opačné modely — obsah
          // tažený doleva odkrývá další (dotykový), kdežto scroll doleva
          // odkrývá předchozí (proto se kolečkem dolů posouvá stránka dolů).
          // Na banneru vedle sebe žijí obě vstupní cesty a musí souhlasit,
          // jinak jedna z nich působí přehozeně; referencí je drag(), protože
          // ten uživatel bere jako „swipe". Vedlejšák: proti karuselům
          // kolekcí (ty jedou nativním scrollem) je banner opačně. Zpět na
          // scrollovací model = otočit tenhle výraz.
          const dir = acc < 0 ? 'next' : 'prev';
          acc = 0;
          lock = true;
          rises = 0;
          lockAt = performance.now();
          this.interrupt(); // rozdělaný přechod se dorazí, ať gesto nepůsobí hluše
          this.go(this.index + (dir === 'next' ? 1 : -1), dir, true);
        },
        { passive: false }
      );
    }

    /**
     * Vlastní kompozitní vrstva pro slidy, které se můžou hnout (aktivní +
     * oba sousedi). Drží se JEN po dobu gesta a dojezdu — trvalé `will-change`
     * je zbytečně zabraná paměť na něčem, co většinu času stojí.
     */
    arm(on) {
      const n = this.slides.length;
      const cur = this.slides[this.index];
      if (!on) {
        // Úklid je schválně přes VŠECHNY slidy: po přehození drží vrstvu i ten,
        // který už sousedem není, a parkovací transform nesmí nikde přežít
        // gesto — jinak by se takový slide při příštím přehození „nevykreslil".
        for (const el of this.slides) {
          el.style.willChange = '';
          if (el.hasAttribute('data-active')) continue;
          el.style.transform = '';
          el.removeAttribute('data-peek');
        }
        return;
      }

      cur.style.willChange = 'transform';
      for (const d of [1, -1]) {
        const el = this.slides[((this.index + d) % n + n) % n];
        if (el === cur) continue;
        el.style.willChange = 'transform';
        // Předehřátí souseda: ostatní slidy jsou `visibility: hidden`, takže se
        // jejich obsah nikdy nevykreslil. Odemkneme ho už na stisk a zaparkujeme
        // o celou šířku stranou (mimo ořez slotu, takže není vidět) — první
        // snímek tažení pak jen posouvá hotovou vrstvu místo aby ji kreslil.
        el.style.transform = `translate3d(${d > 0 ? '' : '-'}100%, 0, 0)`;
        el.setAttribute('data-peek', '');
      }
    }

    /**
     * Dorazí, co zrovna běží, aby mohlo hned začít další gesto.
     * Slidy se tím skokem přesunou do cílového stavu — vizuálně to splyne
     * s rozjezdem nového tažení, protože to stejně hned přepíše transformy.
     */
    interrupt() {
      if (this.settleDone) {
        clearTimeout(this.settleT);
        const done = this.settleDone;
        this.settleDone = null;
        done();
      }
      if (this.animating) this.onEnd();
    }

    /** Dojetí po puštění: buď se soused dotáhne na místo, nebo se vrátí zpět. */
    settle(commit, dir, W) {
      const cur = this.slides[this.index];
      const nb = this.peek;
      if (!nb) { this.removeAttribute('data-dragging'); this.arm(false); this.release('drag'); return; }

      this.busy = true;
      // Dojezd dělá CSS transition (taky kompoziční, ale bez JS na každý
      // snímek), takže se scrubovaná animace musí nejdřív přepsat do inline
      // stylu a zrušit — jinak by přebíjela transition.
      const held = this.dragDx || 0;
      if (this.dragAnimCur) {
        cur.style.transform = `translate3d(${held.toFixed(1)}px, 0, 0)`;
        this.dragAnimCur.cancel();
        this.dragAnimCur = null;
      }
      if (this.dragAnimPeek) {
        nb.style.transform = `translate3d(${((dir === 'next' ? W : -W) + held).toFixed(1)}px, 0, 0)`;
        this.dragAnimPeek.cancel();
        this.dragAnimPeek = null;
      }
      // Atribut dolů DŘÍV, než nasadíme transition — dokud tam je, CSS
      // transitions na slidech vypíná (aby tažení šlo 1:1 za prstem).
      this.removeAttribute('data-dragging');

      // ⚠️ Tečky se překlápějí UŽ TEĎ, ne až v done(). Ten běží po dojezdu
      // (min(speed, 400) + 40 ms), takže indikátor doskakoval znatelně později
      // než slide — tažení působilo, že se karusel „rozmýšlí". Rozhodnutí je
      // přitom hotové v tuhle chvíli, tak ať se to hne se slidem. done() pak
      // zavolá sync(), který nastaví to samé; přepsat správnou hodnotu
      // správnou hodnotou nic nestojí.
      if (commit) {
        const to = this.slides.indexOf(nb);
        this.dots.forEach((d, i) => d.setAttribute('aria-current', i === to ? 'true' : 'false'));
        this.paintDots(nb);
      }

      // Dojezd tažení je svižnější než nastavená délka přechodu: ta je laděná
      // pro automatické prolnutí, kdežto gesto má dojet pod prstem.
      const dur = REDUCED.matches ? 0 : Math.min(this.speed, 400);
      const curTo = commit ? (dir === 'next' ? -W : W) : 0;
      const nbTo = commit ? 0 : dir === 'next' ? W : -W;

      for (const el of [cur, nb]) el.style.transition = `transform ${dur}ms ${EASE}`;
      requestAnimationFrame(() => {
        cur.style.transform = `translate3d(${curTo}px, 0, 0)`;
        nb.style.transform = `translate3d(${nbTo}px, 0, 0)`;
      });

      const done = () => {
        this.settleDone = null;
        for (const el of [cur, nb]) {
          el.style.transition = '';
          el.style.transform = '';
        }
        nb.removeAttribute('data-peek');
        if (commit) {
          cur.removeAttribute('data-active');
          nb.setAttribute('data-active', '');
          this.index = this.slides.indexOf(nb);
          this.sync();
          this.paintDots(nb);
          this.takeOver();
        }
        this.peek = null;
        this.busy = false;
        this.arm(false);
        this.release('drag');
      };

      // Uklizeno buď časovačem, nebo předčasně z interrupt().
      this.settleDone = done;
      clearTimeout(this.settleT);
      this.settleT = setTimeout(() => this.interrupt(), dur + 40);
    }

    /**
     * @param spatial  Přechod MUSÍ jít vodorovně, i když je nastavené prolnutí.
     *
     * ⚠️ Prolnutí nemá směr. Když uživatel táhne dvěma prsty doleva a obrázek
     * se místo posunu jen prolne, není podle čeho poznat, kam to šlo — a gesto
     * pak působí „přehozeně" bez ohledu na to, jaké znaménko čteme z deltaX.
     * Tažení prstem tohle řeší od začátku (jede vodorovně vždycky, viz drag()),
     * posun kolečkem tu výjimku omylem neměl. Efekt ze settings zůstává pro
     * autoplay a tečky, gesto si na svůj přechod sáhne jen na tu chvíli.
     */
    go(to, dir, spatial) {
      const n = this.slides.length;
      const next = ((to % n) + n) % n;
      if (next === this.index || this.animating || this.busy) return;

      // Gesto nemuselo doběhnout (myš puštěná mimo okno) — parkovací transformy
      // po arm(true) by jinak přežily a přicházející slide by zůstal odsunutý.
      this.arm(false);

      const from = this.index;
      const leaving = this.slides[from];
      const entering = this.slides[next];
      const instant = this.dataset.effect === 'none' || REDUCED.matches;

      this.index = next;
      this.sync();
      this.paintDots(entering);

      if (instant) {
        leaving.removeAttribute('data-active');
        entering.setAttribute('data-active', '');
        return;
      }

      this.animating = true;
      // Vrací se v onEnd(), tedy i přes interrupt() a přes pojistkový timeout.
      if (spatial && this.dataset.effect !== 'slide') {
        this.effectHeld = this.dataset.effect;
        this.dataset.effect = 'slide';
      }
      leaving.setAttribute('data-dir', dir);
      entering.setAttribute('data-dir', dir);
      leaving.removeAttribute('data-active');
      leaving.setAttribute('data-leave', '');
      entering.setAttribute('data-active', '');
      entering.setAttribute('data-enter', '');

      this.pending = { leaving, entering };
      // Bez { once: true } a s kontrolou cíle: animationend bublá, takže by nás
      // probudila i animace kteréhokoli potomka slidu.
      entering.addEventListener('animationend', this.onEnd);
      // Pojistka: kdyby animationend nedorazil (přepnutá záložka, zrušená
      // animace), uklidíme natvrdo — jinak by rotátor zůstal zamčený.
      clearTimeout(this.failsafe);
      this.failsafe = setTimeout(this.onEnd, this.speed * 1.5 + 80);
    }

    onEnd(e) {
      if (!this.pending) return;
      const { leaving, entering } = this.pending;
      // Volá se buď z animationend (musí přijít od samotného slidu, ne od
      // potomka), nebo z pojistkového setTimeoutu — ten přichází bez eventu.
      if (e && e.target !== entering) return;

      clearTimeout(this.failsafe);
      entering.removeEventListener('animationend', this.onEnd);
      this.pending = null;
      if (this.effectHeld != null) {
        this.dataset.effect = this.effectHeld;
        this.effectHeld = null;
      }
      for (const el of [leaving, entering]) {
        el.removeAttribute('data-enter');
        el.removeAttribute('data-leave');
        el.removeAttribute('data-dir');
      }
      this.animating = false;
    }

    /** Tečky i chevrony leží nad slidy, takže si barvu berou z toho aktivního. */
    paintDots(slide) {
      const c = slide.style.getPropertyValue('--tk-hero-small-title-color');
      if (c) this.style.setProperty('--tk-hero-dot-color', c);
    }

    sync() {
      this.slides.forEach((s, i) => {
        const on = i === this.index;
        // Neaktivní slide nesmí být v tab pořadí ani ve stromu pro čtečky —
        // jinak by se dalo vytabovat na odkaz, který není vidět.
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        s.tabIndex = on ? 0 : -1;
      });
      this.dots.forEach((d, i) => {
        d.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
    }

    /**
     * Ruční ovládání (tečka, šipka, dotažené tažení) autoplay jen ODLOŽÍ.
     * Dřív se nastavovalo `userTook = true` a rotace se do reloadu stránky
     * nerozjela — na homepage to vypadalo jako rozbitý karusel, protože
     * stačilo jednou švihnout prstem. Teď je to obyčejný hold, který si po
     * chvíli (RESUME_AFTER) sám sundá ruku. Každý další zásah počítá odklad
     * znovu od nuly, takže proklikávání teček rotaci nerozhodí.
     *
     * ⚠️ Samotný `release()` NESTAČÍ. `maybeStart()` po něm založí čerstvý
     * `setInterval`, který odpočítává CELÝ interval od nuly — první přehození
     * by tedy přišlo až za RESUME_AFTER + interval (3 + 4 = 7 s nahoře,
     * 3 + 6 = 9 s dole; přesně tak to bylo nahlášeno). Proto se hned po
     * uvolnění přehodí ručně a timer pak jede dál svým tempem.
     */
    takeOver() {
      this.hold('user');
      clearTimeout(this.resumeT);
      this.resumeT = setTimeout(() => {
        this.release('user');
        // Jen když se autoplay opravdu rozjel — jinou pauzu (fokus, skrytá
        // záložka, mimo viewport) tenhle skok obejít nesmí.
        if (this.timer) this.go(this.index + 1, 'next');
      }, RESUME_AFTER);
    }

    hold(reason) {
      this.holds.add(reason);
      this.stop();
    }

    release(reason) {
      this.holds.delete(reason);
      this.maybeStart();
    }

    maybeStart() {
      if (this.holds.size || !this.interval || REDUCED.matches) return;
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1, 'next'), this.interval);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  if (!customElements.get('tk-hero-rotator')) {
    customElements.define('tk-hero-rotator', TkHeroRotator);
  }

  // TK 2026-10-29 — úprava velikosti titulků kampaňových slidů (fitCampTitles) se
  // přesunula do inline skriptu snippets/tk-hero-camp-fit.liquid: tady jako modul
  // běžela až po stažení souboru (~1,5 s) a titulek viditelně skočil ze 3 řádků na 2.
})();
